import React from 'react';
import { FF_HACKS } from '../data/hacks';
import { V } from '../utils/theme';
import { Icons } from '../components/Icons';
import { useLayout } from '../utils/responsive';
import { Eyebrow, IconTile } from '../components/kit';
import { useFlags } from '../utils/org';

// Shared hub header — eyebrow + title + subtitle, matching the onboarding step headers
function HubHeader({eyebrow,title,sub,style={}}){
  return(
    <div style={{marginBottom:2,...style}}>
      <Eyebrow color={V.accent} style={{marginBottom:6}}>{eyebrow}</Eyebrow>
      <div style={{fontSize:24,fontWeight:800,letterSpacing:"-0.02em",color:V.text,lineHeight:1.15}}>{title}</div>
      <div style={{fontSize:13,color:V.text2,marginTop:4}}>{sub}</div>
    </div>
  );
}

export function LogHub({s,d}){
  const { isDesktop, isTablet } = useLayout();
  const flags=useFlags();
  return(
    <div style={{display:isDesktop?"grid":"flex",flexDirection:"column",gap:14,
      ...(isDesktop?{gridTemplateColumns:"1fr 1fr 1fr",maxWidth:1200}:{})}}>
      <HubHeader eyebrow="Log" title="What are you logging?" sub="Pick a category to start tracking" style={isDesktop?{gridColumn:"1 / -1"}:{}}/>

      {[
        {id:"log_workout",icon:Icons.dumbbell,label:"Workout",desc:"Log sets, reps, weight & RPE",color:V.accent,
          sub:"Templates · Rest Timer · PR Tracking"},
        {id:"log_nutrition",icon:Icons.fork,label:"Nutrition",desc:"Log meals from a 350+ food database",color:V.warn,
          sub:"Auto-calc macros · Water · Sleep",flag:"nutrition"},
        {id:"log_body",icon:Icons.scale,label:"Body Metrics",desc:"Track weight, body fat & measurements",color:V.accent2,
          sub:"Weight · Body Fat · Chest · Waist · Arms"},
      ].filter(item=>!item.flag||flags[item.flag]!==false).map(item=>(
        <button key={item.id} onClick={()=>d({type:"TAB",tab:item.id})} className="hover-lift" style={{
          display:"flex",alignItems:"center",gap:16,padding:18,
          background:V.cardGrad,border:`1px solid ${V.cardBorder}`,borderRadius:18,
          boxShadow:V.shadow,position:"relative",overflow:"hidden",
          cursor:"pointer",WebkitTapHighlightColor:"transparent",textAlign:"left",width:"100%",
        }}>
          {/* Color wash in the icon corner ties the card to its feature color */}
          <div aria-hidden style={{position:"absolute",left:-30,top:-30,width:130,height:130,borderRadius:"50%",
            background:`radial-gradient(circle, ${item.color}14, transparent 70%)`,pointerEvents:"none"}}/>
          <div style={{width:52,height:52,borderRadius:15,background:`linear-gradient(135deg,${item.color}24,${item.color}0a)`,
            border:`1px solid ${item.color}30`,boxShadow:`0 6px 18px -8px ${item.color}50`,
            display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            {item.icon({size:24,color:item.color})}
          </div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:16,fontWeight:700,color:V.text}}>{item.label}</div>
            <div style={{fontSize:12,color:V.text3,marginTop:2}}>{item.desc}</div>
            <div style={{fontSize:10,color:V.text3,marginTop:4,opacity:0.6}}>{item.sub}</div>
          </div>
          {Icons.chevRight({size:18,color:V.text3})}
        </button>
      ))}
    </div>
  );
}

// Items may carry an optional `flag` (org feature key); flagged-off items are hidden.
// Flags are all-on for solo users, so nothing changes outside an org.
export function SectionGrid({title,items,d}){
  const { isDesktop, isTablet } = useLayout();
  const flags=useFlags();
  const visible=items.filter(item=>!item.flag||flags[item.flag]!==false);
  if(!visible.length)return null;
  return(
    <div>
      <Eyebrow style={{marginBottom:8,paddingLeft:2}}>{title}</Eyebrow>
      <div style={{display:"grid",gridTemplateColumns:isDesktop?"1fr 1fr 1fr 1fr":isTablet?"1fr 1fr 1fr":"1fr 1fr",gap:8}}>
        {visible.map(item=>(
          <button key={item.id} onClick={()=>d({type:"TAB",tab:item.id})} className="hover-lift" style={{
            display:"flex",flexDirection:"column",alignItems:"flex-start",gap:8,padding:14,
            background:V.cardGrad,border:`1px solid ${item.badge>0?item.color+"40":V.cardBorder}`,borderRadius:15,
            boxShadow:V.shadow,
            cursor:"pointer",WebkitTapHighlightColor:"transparent",textAlign:"left",position:"relative",
          }}>
            {item.badge>0&&(
              <div style={{position:"absolute",top:10,right:10,minWidth:18,height:18,borderRadius:9,
                background:V.danger,display:"flex",alignItems:"center",justifyContent:"center",
                fontSize:9,fontWeight:800,color:"#fff",padding:"0 4px",boxShadow:`0 2px 8px -2px ${V.danger}80`}}>
                {item.badge>99?"99+":item.badge}
              </div>
            )}
            <IconTile icon={item.icon} color={item.color} size={38}/>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:V.text,lineHeight:1.2}}>{item.label}</div>
              <div style={{fontSize:11,color:V.text3,marginTop:2,lineHeight:1.3}}>{item.desc}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export function TrackHub({s,d}){
  const Section=({title,items})=><SectionGrid title={title} items={items} d={d}/>;

  return(
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <HubHeader eyebrow="Track" title="Your data" sub="History, analytics and reports built from your logs"/>

      <Section title="History" items={[
        {id:"track_calendar",icon:Icons.calendar,label:"Calendar",desc:"Every session by day",color:V.accent},
        {id:"track_nutrition",icon:Icons.fork,label:"Nutrition",desc:"Meals, macros & adherence",color:V.warn,flag:"nutrition"},
        {id:"track_body",icon:Icons.scale,label:"Body",desc:"Weight & measurements",color:V.accent2},
        {id:"track_photos",icon:Icons.scan,label:"Photos",desc:"Progress photos & videos",color:V.purple,flag:"photos"},
      ]}/>

      <Section title="Analytics" items={[
        {id:"track_readiness",icon:Icons.activity,label:"Readiness",desc:"Recovery score trend",color:V.accent},
        {id:"track_analytics",icon:Icons.chart,label:"Analytics",desc:"Trends & charts",color:V.purple},
        {id:"track_exercise_chart",icon:Icons.activity,label:"Exercise Chart",desc:"Weight over time per exercise",color:V.accent},
        {id:"track_duration",icon:Icons.clock,label:"Duration",desc:"Workout length trends",color:"#f472b6"},
        {id:"track_volume",icon:Icons.dumbbell,label:"Volume",desc:"Sets by muscle category",color:"#06b6d4"},
        {id:"track_muscles",icon:Icons.activity,label:"Heat Map",desc:"Muscle balance",color:"#f97316"},
        {id:"track_strength",icon:Icons.trophy,label:"Strength",desc:"Score & rank",color:V.purple},
      ]}/>

      <Section title="Reports" items={[
        {id:"track_summary",icon:Icons.chart,label:"Weekly Report",desc:"Summary card",color:V.accent2,flag:"share_cards"},
        {id:"track_prs",icon:Icons.trophy,label:"PRs",desc:"All-time personal records",color:"#f59e0b"},
        {id:"track_compare",icon:Icons.target,label:"Compare",desc:"Before & after",color:V.purple,flag:"photos"},
        {id:"track_dataguard",icon:Icons.check,label:"Data Guard",desc:"Spot bad entries",color:V.warn},
      ]}/>
    </div>
  );
}

export function PlanHub({s,d}){
  const Section=({title,items})=><SectionGrid title={title} items={items} d={d}/>;

  return(
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <HubHeader eyebrow="Plan" title="Plan ahead" sub="Programs, nutrition and training tools"/>

      <Section title="Training" items={[
        {id:"plan_coach",icon:Icons.zap,label:"AI Coach",desc:"Today's program",color:V.accent2,flag:"ai_coach"},
        {id:"plan_aichat",icon:Icons.activity,label:"AI Chat",desc:"Ask your coach anything",color:"#8b5cf6",flag:"ai_chat"},
        {id:"plan_programs",icon:Icons.calendar,label:"Programs",desc:"4-8 week plans",color:"#06b6d4",flag:"programs"},
        {id:"plan_schedule",icon:Icons.calendar,label:"Schedule",desc:"Weekly plan & calendar",color:V.accent},
        {id:"plan_phases",icon:Icons.calendar,label:"Phases",desc:"Cut / bulk / strength",color:"#a78bfa"},
      ]}/>

      <Section title="Nutrition" items={[
        {id:"plan_mealplan",icon:Icons.fork,label:"Meal Plan",desc:"Auto-generated meals",color:"#f59e0b",flag:"meal_plan"},
        {id:"plan_fastfood",icon:Icons.fork,label:"Fast Food Hacks",desc:`${FF_HACKS.length} macro-friendly meals`,color:"#f97316",flag:"fast_food"},
        {id:"plan_supplements",icon:Icons.plus,label:"Supplements",desc:"Daily checklist",color:"#10b981",flag:"supplements"},
      ]}/>

      <Section title="Tools" items={[
        {id:"plan_goals",icon:Icons.target,label:"Goals",desc:"Milestones & targets",color:"#f43f5e"},
        {id:"plan_onerm",icon:Icons.zap,label:"1RM Calc",desc:"Estimate max",color:V.warn},
        {id:"plan_injuries",icon:Icons.activity,label:"Injuries",desc:"Pain flags & subs",color:V.danger},
        {id:"plan_subs",icon:Icons.refresh,label:"Substitutions",desc:"Alt exercises",color:V.accent},
        {id:"plan_formcheck",icon:Icons.target,label:"Form Check",desc:"Record & review",color:"#f472b6",flag:"form_check"},
      ]}/>
    </div>
  );
}
